import React from 'react';

import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';

import {
    useCurrentTracklog,
    useDebriefApp,
    useDebriefSegments,
    useAnalytics,
    useCurrentAircraft,
    useFlightDataDirectory,
} from 'ff/debrief/hooks';
import { AxisId } from 'ff/debrief/models';

import { getDownSampler, ColorBadge } from './utils';
import { AxisSelectField, AxisSelectForm, axisConfig } from './axis';
import FlightChart from './FlightChart';
import SegmentMarkers from './SegmentMarkers';
import { axisId, convertSpeed } from 'ff/debrief/utils';

const AltitudeSpeedChart = React.forwardRef<HTMLDivElement, {}>((_, ref) => {
    const { isDecluttering } = useDebriefApp();
    const { flightDataDirectory } = useFlightDataDirectory();
    const { points, status } = useCurrentTracklog();
    const { selectedSegment, segmentTree } = useDebriefSegments();
    const { logAxisDataSelected } = useAnalytics();
    const { speedUnit } = useCurrentAircraft();

    const [leftAxis, setLeftAxis] = React.useState<AxisId>('altitude');
    const [rightAxis, setRightAxis] = React.useState<AxisId>('ground_speed');

    const dividerRef = React.useRef<HTMLDivElement>(null);

    const dataPoints = isDecluttering
        ? selectedSegment?.points || points?.data
        : points?.data;

    const isMphAxis = (axis: AxisId) => {
        return (
            ['airspeed', 'ground_speed'].includes(axis) && speedUnit === 'mph'
        );
    };

    const leftDataPoints = React.useMemo<[number[], number[]]>(() => {
        if (!status.isSuccess) return null;

        const [dates, rawData] = getDownSampler(leftAxis)(dataPoints);

        const data = isMphAxis(leftAxis)
            ? rawData.map(value => convertSpeed(value, speedUnit))
            : rawData;

        return [dates, data];
    }, [
        status.isSuccess,
        selectedSegment?.id,
        isDecluttering,
        leftAxis,
        speedUnit,
    ]);

    const rightDataPoints = React.useMemo<[number[], number[]]>(() => {
        if (!status.isSuccess) return null;
        const [dates, rawData] = getDownSampler(rightAxis)(dataPoints);

        const data = isMphAxis(rightAxis)
            ? rawData.map(value => convertSpeed(value, speedUnit))
            : rawData;

        return [dates, data];
    }, [
        status.isSuccess,
        selectedSegment?.id,
        isDecluttering,
        rightAxis,
        speedUnit,
    ]);

    const terrainDataPoints = React.useMemo<[number[], number[]]>(() => {
        if (!status.isSuccess) return null;
        return getDownSampler(axisId.TerrainElevationAxis)(dataPoints);
    }, [status.isSuccess, selectedSegment?.id, isDecluttering, rightAxis]);

    // If the aircraft is configured to mph, replace the label
    const getAxisLabel = (axis: AxisId) =>
        isMphAxis(axis)
            ? axisConfig[axis].label.replace('kts', 'mph')
            : axisConfig[axis].label;

    const handleLeftAxisSelect = (selectedAxis: AxisId) => {
        logAxisDataSelected({
            axis: 'left',
            originalValue: axisConfig[leftAxis].label,
            newValue: axisConfig[selectedAxis].label,
        });

        if (selectedAxis === rightAxis) {
            setRightAxis(leftAxis);
        }
        setLeftAxis(selectedAxis);
    };

    const handleRightAxisSelect = (selectedAxis: AxisId) => {
        logAxisDataSelected({
            axis: 'right',
            originalValue: axisConfig[rightAxis].label,
            newValue: axisConfig[selectedAxis].label,
        });

        if (selectedAxis === leftAxis) {
            setLeftAxis(rightAxis);
        }
        setRightAxis(selectedAxis);
    };

    if (!points) return null;
    return (
        <Stack ref={ref} sx={{ position: 'relative', px: 1, height: 270 }}>
            <AxisSelectForm ref={dividerRef}>
                <AxisSelectField
                    selectedAxisId={leftAxis}
                    onAxisSelect={handleLeftAxisSelect}
                >
                    {flightDataDirectory.getPlottableFields().map(axis => (
                        <MenuItem key={axis} value={axis}>
                            <ColorBadge color={axisConfig[axis].color} />
                            {getAxisLabel(axis)}
                        </MenuItem>
                    ))}
                </AxisSelectField>
                <AxisSelectField
                    selectedAxisId={rightAxis}
                    onAxisSelect={handleRightAxisSelect}
                >
                    {flightDataDirectory.getPlottableFields().map(axis => (
                        <MenuItem key={axis} value={axis}>
                            <ColorBadge color={axisConfig[axis].color} />
                            {getAxisLabel(axis)}
                        </MenuItem>
                    ))}
                </AxisSelectField>
            </AxisSelectForm>
            <FlightChart
                timestamps={leftDataPoints[0]}
                leftAxisId={leftAxis}
                rightAxisId={rightAxis}
                leftAxisData={leftDataPoints[1]}
                rightAxisData={rightDataPoints[1]}
                terrainData={terrainDataPoints[1]}
                tooltipBounds={dividerRef}
                speedUnit={speedUnit}
            >
                {segmentTree && (
                    <SegmentMarkers
                        currentSegment={selectedSegment}
                        segments={segmentTree.getLeafNodes(
                            selectedSegment && {
                                start: leftDataPoints[0].at(0),
                                end: leftDataPoints[0].at(-1),
                            }
                        )}
                    />
                )}
            </FlightChart>
        </Stack>
    );
});

AltitudeSpeedChart.displayName = 'AltitudeSpeedChart';

export default AltitudeSpeedChart;
